import { createSelector } from '@reduxjs/toolkit';
import { getImageBounds, isImageWithinBounds } from '../../utils/personImageUtils';

// Base selectors with safe defaults
export const selectPersonImageState = (state) => state.personImage || {};

export const selectPersonImage = (state) => state.personImage?.image || null;
export const selectActivePersonImageId = (state) => state.personImage?.activePersonImageId || null;
export const selectOriginalImage = (state) => state.personImage?.originalImage || null;
export const selectScale = (state) => state.personImage?.scale || 1.0;

// Simple selectors with defaults - not using createSelector to avoid identity function warnings
// These return stable references when values exist in state
const DEFAULT_POSITION = { x: 0, y: 0 };
export const selectPosition = (state) => state.personImage?.position || DEFAULT_POSITION;

const DEFAULT_METADATA = {};
export const selectMetadata = (state) => state.personImage?.metadata || DEFAULT_METADATA;

const DEFAULT_CANVAS_SIZE = { width: 800, height: 600 };
export const selectCanvasSize = (state) => state.personImage?.canvasSize || DEFAULT_CANVAS_SIZE;

const DEFAULT_SETTINGS = {
  autoCenter: true,
  autoScale: true,
  preserveAspectRatio: true,
  maxScale: 2.0,
  minScale: 0.1
};
export const selectSettings = (state) => state.personImage?.settings || DEFAULT_SETTINGS;

// History selectors - simple selectors with defaults
const DEFAULT_HISTORY = [];
export const selectHistory = (state) => state.personImage?.history || DEFAULT_HISTORY;
export const selectMaxHistorySize = (state) => state.personImage?.maxHistorySize || 10;

// Virtual try-on selectors
export const selectIsVirtualTryOnResult = (state) => state.personImage?.isVirtualTryOnResult || false;
export const selectVirtualTryOnMetadata = (state) => state.personImage?.virtualTryOnMetadata;
export const selectOriginalPersonImageDeleted = (state) => state.personImage?.originalPersonImageDeleted || false;

// UI state selectors
export const selectIsLoading = (state) => state.personImage?.isLoading || false;
export const selectUploadProgress = (state) => state.personImage?.uploadProgress || 0;
export const selectError = (state) => state.personImage?.error;
export const selectIsCanvasIntegrated = (state) => state.personImage?.isCanvasIntegrated || false;

// Computed selectors
export const selectHasPersonImage = createSelector(
  [selectActivePersonImageId],
  (activePersonImageId) => !!activePersonImageId
);

export const selectHasOriginalImage = createSelector(
  [selectOriginalImage],
  (originalImage) => !!originalImage
);

export const selectCanRevertToOriginal = createSelector(
  [selectPersonImage, selectOriginalImage, selectIsVirtualTryOnResult],
  (image, originalImage, isVirtualResult) =>
    !!originalImage && !!image && isVirtualResult && image !== originalImage
);

export const selectImageDimensions = createSelector(
  [selectMetadata],
  (metadata) => (metadata && metadata.originalDimensions) || { width: 0, height: 0 }
);

export const selectProcessedDimensions = createSelector(
  [selectMetadata],
  (metadata) => (metadata && (metadata.processedDimensions || metadata.originalDimensions)) || { width: 0, height: 0 }
);

export const selectIsBackgroundRemoved = createSelector(
  [selectMetadata],
  (metadata) => (metadata && metadata.backgroundRemoved) || false
);

export const selectImageBounds = createSelector(
  [selectPosition, selectImageDimensions, selectScale],
  (position, dimensions, scale) => {
    if (!dimensions.width || !dimensions.height) {
      return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }
    return getImageBounds(position, dimensions, scale);
  }
);

export const selectIsImageWithinCanvas = createSelector(
  [selectPosition, selectImageDimensions, selectScale, selectCanvasSize],
  (position, dimensions, scale, canvasSize) => {
    if (!dimensions.width || !dimensions.height || !canvasSize.width || !canvasSize.height) {
      return true; // Assume valid if we don't have dimensions
    }
    return isImageWithinBounds(position, dimensions, scale, canvasSize);
  }
);

export const selectScaleInfo = createSelector(
  [selectScale, selectSettings],
  (scale, settings) => ({
    current: scale,
    min: settings.minScale,
    max: settings.maxScale,
    canScaleUp: scale < settings.maxScale,
    canScaleDown: scale > settings.minScale,
    percentage: Math.round(scale * 100)
  })
);

export const selectImageInfo = createSelector(
  [selectPersonImage, selectMetadata, selectPosition, selectScale, selectIsVirtualTryOnResult],
  (image, metadata, position, scale, isVirtualResult) => ({
    hasImage: !!image,
    fileName: (metadata && metadata.fileName) || 'Unknown',
    fileSize: (metadata && metadata.fileSize) || 0,
    uploadTimestamp: metadata && metadata.uploadTimestamp,
    backgroundRemoved: (metadata && metadata.backgroundRemoved) || false,
    isVirtualResult,
    position,
    scale,
    dimensions: (metadata && metadata.originalDimensions) || { width: 0, height: 0 }
  })
);

export const selectCanvasInfo = createSelector(
  [selectCanvasSize, selectImageBounds, selectIsImageWithinCanvas],
  (canvasSize, imageBounds, isWithinCanvas) => ({
    size: canvasSize,
    imageBounds,
    isImageWithinCanvas: isWithinCanvas,
    imageArea: imageBounds.width * imageBounds.height,
    canvasArea: canvasSize.width * canvasSize.height,
    imageCoverage: canvasSize.width && canvasSize.height
      ? (imageBounds.width * imageBounds.height) / (canvasSize.width * canvasSize.height)
      : 0
  })
);

export const selectUploadStatus = createSelector(
  [selectIsLoading, selectUploadProgress, selectError],
  (isLoading, progress, error) => ({
    isLoading,
    progress,
    error,
    hasError: !!error,
    isComplete: !isLoading && !error && progress === 0
  })
);

// Settings selectors
export const selectAutoCenter = createSelector(
  [selectSettings],
  (settings) => settings.autoCenter
);

export const selectAutoScale = createSelector(
  [selectSettings],
  (settings) => settings.autoScale
);

export const selectPreserveAspectRatio = createSelector(
  [selectSettings],
  (settings) => settings.preserveAspectRatio
);

// Complex computed selectors
export const selectPersonImageDisplayProps = createSelector(
  [
    selectPersonImage,
    selectActivePersonImageId,
    selectPosition,
    selectScale,
    selectIsBackgroundRemoved,
    selectIsVirtualTryOnResult,
    selectCanvasSize,
    selectIsCanvasIntegrated
  ],
  (image, activePersonImageId, position, scale, backgroundRemoved, isVirtualResult, canvasSize, isCanvasIntegrated) => ({
    personImage: image,
    activePersonImageId,
    position,
    scale,
    backgroundRemoved,
    isVirtualTryOnResult: isVirtualResult,
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
    isCanvasIntegrated
  })
);

export const selectPersonImageActions = createSelector(
  [selectHasPersonImage, selectCanRevertToOriginal, selectScaleInfo, selectIsImageWithinCanvas],
  (hasImage, canRevert, scaleInfo, isWithinCanvas) => ({
    canClear: hasImage,
    canRevert: canRevert,
    canCenter: hasImage,
    canScaleUp: scaleInfo.canScaleUp,
    canScaleDown: scaleInfo.canScaleDown,
    canResetScale: hasImage,
    needsRepositioning: hasImage && !isWithinCanvas
  })
);

// History computed selectors
export const selectHasHistory = createSelector(
  [selectHistory],
  (history) => Array.isArray(history) && history.length > 0
);

export const selectHistoryCount = createSelector(
  [selectHistory],
  (history) => Array.isArray(history) ? history.length : 0
);

export const selectRecentHistory = createSelector(
  [selectHistory],
  (history) => Array.isArray(history) ? history.slice(0, 5) : [] // Get most recent 5 items
);

export const selectHistoryInfo = createSelector(
  [selectHistory, selectMaxHistorySize],
  (history, maxSize) => {
    const safeHistory = Array.isArray(history) ? history : [];
    const safeMaxSize = typeof maxSize === 'number' ? maxSize : 10;
    return {
      count: safeHistory.length,
      maxSize: safeMaxSize,
      hasHistory: safeHistory.length > 0,
      isFull: safeHistory.length >= safeMaxSize,
      canAddMore: safeHistory.length < safeMaxSize
    };
  }
);

export const selectHistoryById = createSelector(
  [selectHistory, (state, historyId) => historyId],
  (history, historyId) => {
    if (!Array.isArray(history) || !historyId) return null;
    return history.find(item => item && item.id === historyId) || null;
  }
);